import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { useRole } from "./RoleContext";
import { ROLE_PERMISSIONS } from "./permissions";
import { SettingsService, RolePermissionsSettings } from "@/services/settings.service";
import { expandSettingsLabels, COVERED_KEYS } from "./permissionsMapping";

interface PermissionsContextValue {
  hasPermission: (key: string) => boolean;
  loading: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  hasPermission: () => false,
  loading: true,
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { role } = useRole();
  const [permSet, setPermSet] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    function applyMatrix(matrix: RolePermissionsSettings) {
      if (cancelled) return;
      if (role === "super_admin") {
        setPermSet(new Set(["*"]));
      } else {
        const roleLabels: string[] = matrix[role] ?? [];
        const dynamicKeys = expandSettingsLabels(roleLabels);
        const staticKeys = (ROLE_PERMISSIONS[role] ?? []).filter(
          (k) => !COVERED_KEYS.has(k),
        );
        setPermSet(new Set([...dynamicKeys, ...staticKeys]));
      }
      setLoading(false);
    }

    setLoading(true);

    SettingsService.getRolePermissions()
      .then(applyMatrix)
      .catch(() => {
        if (!cancelled) {
          // Fallback: use static ROLE_PERMISSIONS
          setPermSet(new Set(ROLE_PERMISSIONS[role] ?? []));
          setLoading(false);
        }
      });

    // Date.now() suffix prevents React StrictMode double-subscribe errors
    const channel = supabase
      .channel(`role-permissions-watch-${Date.now()}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "app_settings",
          filter: "setting_type=eq.role_permissions",
        },
        (payload) => {
          if (payload.new && "settings_json" in payload.new) {
            applyMatrix(payload.new.settings_json as RolePermissionsSettings);
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [role]);

  const hasPermission = (key: string): boolean => {
    if (role === "super_admin") return true;
    return permSet.has("*") || permSet.has(key);
  };

  return (
    <PermissionsContext.Provider value={{ hasPermission, loading }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextValue {
  return useContext(PermissionsContext);
}
